import { useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import Sidebar, { isActive } from "./Sidebar";
import { usePermissions } from "./permissions";
import UserMenu from "./UserMenu";
import FlashMessages from "./FlashMessages";
import { Logo, Wordmark } from "./Brand";
import {
  InvoicesIcon,
  MenuIcon,
  PaymentsIcon,
  PurchasesIcon,
  ReceiptsIcon,
} from "./icons";

// The four lists the counter lives in, one click from anywhere. They are in the sidebar too,
// but the sidebar is closed on a laptop-width window and these are the four that get opened
// twenty times a day — so they sit on the bar itself, where nothing has to be opened first.
//
// Four and no more: the value of this strip is that it can be hit without reading it, and
// that stops being true the moment it becomes a menu.
const QUICK_ACTIONS = [
  { label: "Invoice", href: "/invoices", icon: InvoicesIcon, can: "invoices" },
  { label: "Receipt", href: "/receipts", icon: ReceiptsIcon, can: "receipts" },
  { label: "Payment", href: "/payments", icon: PaymentsIcon, can: "payments" },
  { label: "Purchase", href: "/purchases", icon: PurchasesIcon, can: "purchases" },
];

// Persistent shell for every signed-in page: a full-width top bar carrying the
// brand and the account chip, with the sidebar hanging below it on the left.
// The shell survives Inertia navigations, so the top bar and sidebar are laid
// out once rather than remounted on every page.
export default function AppLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { url } = usePage();
  const { read } = usePermissions();

  // The strip is a set of shortcuts into four lists. An account that cannot open one is not
  // shown its chip — the value of these is that they can be hit without reading them, and a
  // chip that bounces you to the dashboard is worse than one that isn't there.
  const actions = QUICK_ACTIONS.filter(({ can }) => read(can));

  return (
    <div className="min-h-dvh bg-slate-100">
      <header className="fixed inset-x-0 top-0 z-40 border-b border-slate-200 bg-white/90 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="relative flex h-16 items-center gap-3 px-4 sm:px-6">
          <button
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            className="-ml-1 rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 lg:hidden"
          >
            <MenuIcon />
          </button>

          {/* The mark alone until the bar is wide enough for the words as well. Between a
              phone and a desktop the quick actions run across the middle of the header, and
              the company name was the other block they ran into — the logo still says whose
              yard this is and still goes home, so the words are what give way. */}
          <Link href="/" className="flex min-w-0 items-center gap-3">
            <Logo size={38} />
            <Wordmark
              className="hidden lg:block"
              nameClass="text-[15px] leading-tight text-slate-900"
              suffixClass="text-[11px] uppercase tracking-wide text-slate-400"
            />
          </Link>

          {/* Centred on the bar itself rather than on whatever room the brand and the
              account chip leave between them — those two are different widths on every
              account, and a strip that drifted with them would never sit still.
              Absolutely placed for that reason, and the full-width track it needs is
              click-through so it can't swallow a press meant for either of them. */}
          <nav
            aria-label="Quick actions"
            className="pointer-events-none absolute inset-x-0 flex justify-center"
          >
            <div className="pointer-events-auto flex items-center gap-1 md:gap-1.5">
              {actions.map(({ label, href, icon: ActionIcon }) => {
                // Lit the same way the sidebar lights it, off the same helper, so the two
                // never disagree about which section you are looking at.
                const here = isActive(url, href);

                return (
                  <Link
                    key={href}
                    href={href}
                    aria-current={here ? "page" : undefined}
                    // Titled as well as labelled: the label is the first thing to go when
                    // the window narrows, and an icon nobody can name is a button nobody
                    // presses.
                    title={label}
                    // The border is drawn whether or not the section is current: four
                    // outlined chips read as four buttons at a glance, where four bare
                    // words read as a sentence you have to parse before you can aim. The
                    // states then only have to say which one you are in, not that they
                    // are pressable at all.
                    className={`group inline-flex h-9 items-center gap-1.5 rounded-lg border px-2 text-[13px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700 md:px-2.5 lg:gap-2 lg:px-3.5 lg:text-sm ${
                      // Three tiers that can be told apart at arm's length: plain on
                      // white, a teal wash under the cursor, a filled chip for the
                      // section you are actually in.
                      here
                        ? "border-brand-300 bg-brand-100 text-brand-900"
                        : "border-slate-200 bg-white text-slate-700 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800 active:bg-brand-100"
                    }`}
                  >
                    <ActionIcon
                      className={`h-[18px] w-[18px] shrink-0 transition-colors ${
                        here ? "text-brand-700" : "text-brand-600 group-hover:text-brand-700"
                      }`}
                    />
                    {/* Named from 768 up. Below it the row is still here — these are the
                        four things the counter does all day and they should never be a
                        menu away — but there is no room beside the mark and the account
                        chip for four words, so the icons stand alone and `title` names
                        them. */}
                    <span className="hidden md:inline">{label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          <div className="ml-auto">
            <UserMenu />
          </div>
        </div>
      </header>

      <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)} />
      <FlashMessages />

      <div className="pt-topbar lg:pl-64">
        {/* A flex column at least as tall as what is left of the window, so a page can
            push something to the foot of the screen with mt-auto rather than leaving it
            floating halfway up on a short page. Costs nothing on a page that doesn't. */}
        <main className="min-h-below-topbar flex flex-col px-4 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-5 sm:py-8">
          {/* Wide enough that a full-width table gets the room, still capped so
              text on an ultrawide monitor doesn't run the width of the desk. */}
          <div className="mx-auto flex w-full max-w-[1600px] flex-1 flex-col">{children}</div>
        </main>
      </div>
    </div>
  );
}
